// =============================================================================
// Lumen Viewer — measurement primitives
// =============================================================================
// A measurement is { kind, points: [[x,y],...], value_mm?, value_deg?, label }
// stored server-side. Pixel coordinates are in IMAGE space (not canvas).
// The calibration ratio (px → mm) lives on the rendered study.

export const KIND = {
  DISTANCE:    'distance',
  ANGLE:       'angle',
  COBB:        'cobb',
  ELLIPSE:     'ellipse',
  FREEHAND:    'freehand',
  CALIBRATION: 'calibration',
};

export function pxDist(a, b) {
  const dx = a[0] - b[0], dy = a[1] - b[1];
  return Math.sqrt(dx * dx + dy * dy);
}

// 3-point interior angle (degrees). b is the vertex.
export function interiorAngle(a, b, c) {
  const v1 = [a[0] - b[0], a[1] - b[1]];
  const v2 = [c[0] - b[0], c[1] - b[1]];
  const dot = v1[0] * v2[0] + v1[1] * v2[1];
  const n1 = Math.hypot(v1[0], v1[1]), n2 = Math.hypot(v2[0], v2[1]);
  if (n1 === 0 || n2 === 0) return 0;
  const cos = Math.max(-1, Math.min(1, dot / (n1 * n2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

// Angle between two lines (each a pair of points). Used for Cobb.
export function lineLineAngle(l1, l2) {
  const a1 = Math.atan2(l1[1][1] - l1[0][1], l1[1][0] - l1[0][0]);
  const a2 = Math.atan2(l2[1][1] - l2[0][1], l2[1][0] - l2[0][0]);
  let deg = Math.abs((a1 - a2) * 180 / Math.PI);
  if (deg > 90) deg = 180 - deg;
  return deg;
}

// Recompute value_mm / value_deg from points + calibration.
export function recompute(m, mmPerPx) {
  const pts = m.points || [];
  switch (m.kind) {
    case KIND.DISTANCE: {
      if (pts.length < 2) return { value_mm: null };
      const px = pxDist(pts[0], pts[1]);
      return { value_mm: mmPerPx ? px * mmPerPx : null, value_px: px };
    }
    case KIND.ANGLE: {
      if (pts.length < 3) return { value_deg: null };
      return { value_deg: interiorAngle(pts[0], pts[1], pts[2]) };
    }
    case KIND.COBB: {
      if (pts.length < 4) return { value_deg: null };
      return { value_deg: lineLineAngle([pts[0], pts[1]], [pts[2], pts[3]]) };
    }
    case KIND.ELLIPSE: {
      if (pts.length < 2) return { value_mm: null };
      const w = Math.abs(pts[1][0] - pts[0][0]);
      const h = Math.abs(pts[1][1] - pts[0][1]);
      const areaPx = Math.PI * (w / 2) * (h / 2);
      return { value_area_mm2: mmPerPx ? areaPx * mmPerPx * mmPerPx : null };
    }
    case KIND.CALIBRATION: {
      if (pts.length < 2) return { value_mm: null };
      return { value_mm: m.value_mm ?? null }; // set by user
    }
    default:
      return {};
  }
}

// How many points a given measurement kind expects before it's "complete".
export function pointsRequired(kind) {
  return ({
    [KIND.DISTANCE]:    2,
    [KIND.ANGLE]:       3,
    [KIND.COBB]:        4,
    [KIND.ELLIPSE]:     2,
    [KIND.FREEHAND]:    Infinity,
    [KIND.CALIBRATION]: 2,
  })[kind] ?? 2;
}

// Format a value for display in the overlay.
export function formatValue(m) {
  if (m.value_mm != null)    return `${m.value_mm.toFixed(1)} mm`;
  if (m.value_deg != null)   return `${m.value_deg.toFixed(1)}°`;
  if (m.value_area_mm2 != null) return `${m.value_area_mm2.toFixed(0)} mm²`;
  if (m.value_px != null)    return `${Math.round(m.value_px)} px`;
  return '—';
}
